import { safeLocalStorage } from '../utils/storage.js';

function debounce(fn, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  };
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.boundHandlers = {};
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.form = this.querySelector('[data-facets-form]');
    this.drawer = this.querySelector('#FacetsDrawer');
    this.loading = this.querySelector('[data-facets-loading]');

    
    if (this.drawer) {
      document.body.appendChild(this.drawer);
    }

    
    this.boundHandlers.onFormChange = debounce(this.onFormChange.bind(this), 500);
    this.boundHandlers.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.boundHandlers.onKeydown = this.onKeydown.bind(this);
    this.boundHandlers.onPopState = this.onPopState.bind(this);

    this.bindEvents();
    this.setupHistoryListener();
  }

  disconnectedCallback() {
    
    document.removeEventListener('keydown', this.boundHandlers.onKeydown);
    window.removeEventListener('popstate', this.boundHandlers.onPopState);

    
    if (this.drawer && this.drawer.parentNode === document.body) {
      this.drawer.remove();
    }
  }

  bindEvents() {
    
    if (this.form) {
      this.form.addEventListener('input', this.boundHandlers.onFormChange);
    }

    
    const openBtn = this.querySelector('[data-facets-open]');
    if (openBtn) {
      openBtn.addEventListener('click', () => this.openDrawer());
    }

    
    if (this.drawer) {
      this.drawer.querySelectorAll('[data-facets-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeDrawer());
      });

      
      const applyBtn = this.drawer.querySelector('[data-facets-apply]');
      if (applyBtn) {
        applyBtn.addEventListener('click', () => this.closeDrawer());
      }
    }

    
    this.querySelectorAll('[data-facet-remove], [data-facet-clear-all]').forEach((link) => {
      link.addEventListener('click', this.boundHandlers.onActiveFilterClick);
    });

    
    const sortSelect = this.querySelector('#SortBy');
    if (sortSelect) {
      sortSelect.addEventListener('change', this.boundHandlers.onFormChange);
    }

    
    const sortSelectMobile = this.querySelector('#SortByMobile');
    if (sortSelectMobile) {
      sortSelectMobile.addEventListener('change', (e) => {
        
        if (sortSelect) {
          sortSelect.value = e.target.value;
        }
      });
    }

    
    document.addEventListener('keydown', this.boundHandlers.onKeydown);

    
    this.querySelectorAll('.facets-disclosure').forEach((details) => {
      details.addEventListener('toggle', () => {
        const icon = details.querySelector('.ph-caret-down');
        if (icon) {
          icon.style.transform = details.open ? 'rotate(180deg)' : '';
        }
      });
    });

    
    this.setupViewToggle();
  }

  setupViewToggle() {
    const viewBtns = this.querySelectorAll('[data-view-toggle]');
    const wrapper = document.querySelector('[data-collection-wrapper]');

    if (!viewBtns.length || !wrapper) return;

    
    const savedView = safeLocalStorage('collection-view') || 'grid';
    wrapper.dataset.view = savedView;
    viewBtns.forEach((btn) => {
      const isActive = btn.dataset.viewToggle === savedView;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    
    viewBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.viewToggle;
        const currentView = wrapper.dataset.view;

        
        if (view === currentView) return;

        const grid = wrapper.querySelector('[data-collection-content]');
        const shouldAnimate = typeof window.shouldAnimate === 'function' && window.shouldAnimate();
        const gsap = window.gsap || window.pieces?.gsap;

        
        viewBtns.forEach((b) => {
          const isActive = b.dataset.viewToggle === view;
          b.classList.toggle('is-active', isActive);
          b.setAttribute('aria-pressed', isActive);
        });

        if (shouldAnimate && gsap && grid) {
          
          const startHeight = grid.offsetHeight;

          
          gsap.to(grid, {
            opacity: 0,
            duration: 0.2,
            ease: 'power2.in',
            onComplete: () => {
              
              grid.style.height = `${startHeight}px`;
              grid.style.overflow = 'hidden';

              // Change layout
              wrapper.dataset.view = view;

              // Get new height
              grid.style.height = 'auto';
              const endHeight = grid.offsetHeight;
              grid.style.height = `${startHeight}px`;

              // Animate height and fade in
              gsap.to(grid, {
                height: endHeight,
                duration: 0.4,
                ease: 'power3.inOut',
              });

              gsap.to(grid, {
                opacity: 1,
                duration: 0.3,
                delay: 0.2,
                ease: 'power2.out',
                onComplete: () => {
                  // Clean up
                  grid.style.height = '';
                  grid.style.overflow = '';

                  // Refresh Lenis
                  if (window.pieces?.lenis) {
                    window.pieces.lenis.resize();
                  }
                },
              });
            },
          });
        } else {
          // No animation
          wrapper.dataset.view = view;
          if (window.pieces?.lenis) {
            window.pieces.lenis.resize();
          }
        }

        // Save preference
        safeLocalStorage('collection-view', view);
      });
    });
  }

  setupHistoryListener() {
    // Store initial search params
    FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
    FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

    // Listen for browser back/forward
    window.addEventListener('popstate', this.boundHandlers.onPopState);
  }

  /**
   * Handle keydown events (escape to close drawer)
   */
  onKeydown(e) {
    if (e.key === 'Escape' && this.isDrawerOpen()) {
      this.closeDrawer();
    }
  }

  /**
   * Handle browser back/forward navigation
   */
  onPopState(event) {
    const searchParams = event.state?.searchParams ?? FacetFiltersForm.searchParamsInitial;
    if (searchParams === FacetFiltersForm.searchParamsPrev) return;
    this.renderPage(searchParams, false);
  }

  isDrawerOpen() {
    return this.drawer && !this.drawer.hasAttribute('inert');
  }

  openDrawer() {
    if (!this.drawer) return;

    // Enable interactions
    this.drawer.removeAttribute('inert');
    this.drawer.classList.add('is-open');

    // Animate in
    const backdrop = this.drawer.querySelector('.facets-drawer__backdrop');
    const panel = this.drawer.querySelector('.facets-drawer__panel');

    if (backdrop) {
      backdrop.style.opacity = '1';
    }
    if (panel) {
      panel.style.transform = 'translateX(0)';
    }

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Stop Lenis
    if (window.pieces?.lenis) {
      window.pieces.lenis.stop();
    }

    // Update button state
    const openBtn = this.querySelector('[data-facets-open]');
    if (openBtn) {
      openBtn.setAttribute('aria-expanded', 'true');
    }

    // Focus first focusable element
    requestAnimationFrame(() => {
      const closeBtn = this.drawer.querySelector('[data-facets-close]');
      if (closeBtn) closeBtn.focus();
    });
  }

  closeDrawer() {
    if (!this.drawer) return;

    // Animate out
    const backdrop = this.drawer.querySelector('.facets-drawer__backdrop');
    const panel = this.drawer.querySelector('.facets-drawer__panel');

    if (backdrop) {
      backdrop.style.opacity = '0';
    }
    if (panel) {
      panel.style.transform = 'translateX(-100%)';
    }

    // Wait for animation then disable interactions
    setTimeout(() => {
      this.drawer.setAttribute('inert', '');
      this.drawer.classList.remove('is-open');

      // Restore body scroll
      document.body.style.overflow = '';

      // Start Lenis
      if (window.pieces?.lenis) {
        window.pieces.lenis.start();
      }
    }, 300);

    // Update button state
    const openBtn = this.querySelector('[data-facets-open]');
    if (openBtn) {
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.focus();
    }
  }

  onFormChange(event) {
    const formData = new FormData(this.form);

    // Explicitly add sort_by from desktop select (it's outside the form in DOM due to drawer move)
    const sortSelect = document.querySelector('#SortBy');
    if (sortSelect && sortSelect.value) {
      formData.set('sort_by', sortSelect.value);
    }

    const searchParams = new URLSearchParams(formData).toString();
    this.renderPage(searchParams);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url = event.currentTarget.href;
    const searchParams = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
    this.renderPage(searchParams);
  }

  async renderPage(searchParams, updateURL = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    // Show loading state
    this.showLoading();

    try {
      // Fetch filtered content
      const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
      const response = await fetch(url);
      const html = await response.text();

      // Parse response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update product grid
      this.updateProductGrid(doc);

      // Update filters
      this.updateFilters(doc);

      // Update active facets
      this.updateActiveFacets(doc);

      // Update product count
      this.updateProductCount(doc);

      // Update pagination (load more button with new URLs)
      this.updatePagination(doc);

      // Update URL
      if (updateURL) {
        this.updateURL(searchParams);
      }

      // Refresh Lenis scroll bounds
      if (window.pieces?.lenis) {
        window.pieces.lenis.resize();
      }
    } catch {
      // Silently handle filter errors - UI will remain in previous state
    } finally {
      this.hideLoading();
    }
  }

  updateProductGrid(doc) {
    const newContent = doc.querySelector('[data-collection-content]');
    const currentContent = document.querySelector('[data-collection-content]');

    if (newContent && currentContent) {
      // Add transition class
      const wrapper = document.querySelector('[data-collection-wrapper]');
      if (wrapper) {
        wrapper.classList.add('is-transitioning');
      }

      // Replace content safely
      currentContent.replaceChildren(...newContent.cloneNode(true).childNodes);

      // Trigger animation for new items
      requestAnimationFrame(() => {
        if (wrapper) {
          wrapper.classList.remove('is-transitioning');
          // Re-trigger item animations
          this.animateNewItems(currentContent);
        }
      });
    }
  }

  animateNewItems(container) {
    const items = container.querySelectorAll('[data-collection-item], [data-product-card]');

    if (typeof gsap !== 'undefined') {
      items.forEach((item, index) => {
        const imgWrap = item.querySelector('.product-card-image');
        const titleSpans = item.querySelectorAll('.product-card-title .overflow-hidden span');
        const priceSpans = item.querySelectorAll('.product-card-price .overflow-hidden span');

        // Set initial states
        if (imgWrap) {
          gsap.set(imgWrap, { clipPath: 'inset(0 100% 0 0)' });
        }
        if (titleSpans.length) {
          gsap.set(titleSpans, { yPercent: 100 });
        }
        if (priceSpans.length) {
          gsap.set(priceSpans, { yPercent: 100 });
        }

        // Animate in with stagger
        const delay = (index % 4) * 0.1;
        gsap.delayedCall(delay, () => {
          const tl = gsap.timeline();

          if (imgWrap) {
            tl.to(
              imgWrap,
              {
                clipPath: 'inset(0 0% 0 0)',
                duration: 1.2,
                ease: 'expo.inOut',
              },
              0,
            );
          }
          if (titleSpans.length) {
            tl.to(
              titleSpans,
              {
                yPercent: 0,
                duration: 0.8,
                ease: 'power4.out',
                stagger: 0.05,
              },
              0.2,
            );
          }
          if (priceSpans.length) {
            tl.to(
              priceSpans,
              {
                yPercent: 0,
                duration: 0.6,
                ease: 'power4.out',
                stagger: 0.05,
              },
              0.3,
            );
          }
        });
      });
    }
  }

  updateFilters(doc) {
    const newFilters = doc.querySelector('.facets-drawer__filters');
    const currentFilters = this.querySelector('.facets-drawer__filters');

    if (newFilters && currentFilters) {
      // Preserve open states
      const openFilters = [...currentFilters.querySelectorAll('details[open]')].map((d) => d.id);

      currentFilters.replaceChildren(...newFilters.cloneNode(true).childNodes);

      // Restore open states
      openFilters.forEach((id) => {
        const details = currentFilters.querySelector(`#${id}`);
        if (details) details.open = true;
      });

      // Re-bind disclosure animations
      currentFilters.querySelectorAll('.facets-disclosure').forEach((details) => {
        details.addEventListener('toggle', () => {
          const icon = details.querySelector('.ph-caret-down');
          if (icon) {
            icon.style.transform = details.open ? 'rotate(180deg)' : '';
          }
        });
      });
    }
  }

  updateActiveFacets(doc) {
    const newActive = doc.querySelector('#ActiveFacets');
    const currentActive = this.querySelector('#ActiveFacets');

    if (newActive && currentActive) {
      currentActive.outerHTML = newActive.outerHTML;

      // Re-bind click handlers
      this.querySelectorAll('[data-facet-remove], [data-facet-clear-all]').forEach((link) => {
        link.addEventListener('click', this.boundHandlers.onActiveFilterClick);
      });
    }

    // Update filter button count
    const openBtn = this.querySelector('[data-facets-open]');
    if (openBtn) {
      const newBtn = doc.querySelector('[data-facets-open]');
      if (newBtn) {
        openBtn.replaceChildren(...newBtn.cloneNode(true).childNodes);
      }
    }
  }

  updateProductCount(doc) {
    const newCount = doc.querySelector('#ProductCount');
    const currentCount = this.querySelector('#ProductCount');

    if (newCount && currentCount) {
      currentCount.replaceChildren(...newCount.cloneNode(true).childNodes);
    }
  }

  updatePagination(doc) {
    // Update load more pagination
    const newPagination = doc.querySelector('[data-pagination-load-more]');
    const currentPagination = document.querySelector('[data-pagination-load-more]');

    if (currentPagination) {
      if (newPagination) {
        currentPagination.replaceChildren(...newPagination.cloneNode(true).childNodes);
      } else {
        // No more pagination needed (fewer results than page size)
        currentPagination.replaceChildren();
      }
    }

    // Update prev/next pagination
    const newPrevNext = doc.querySelector('.collection-wrapper nav');
    const currentPrevNext = document.querySelector('.collection-wrapper nav');

    if (currentPrevNext && newPrevNext) {
      currentPrevNext.replaceChildren(...newPrevNext.cloneNode(true).childNodes);
    } else if (currentPrevNext && !newPrevNext) {
      currentPrevNext.remove();
    }
  }

  updateURL(searchParams) {
    // Remove section_id from URL params - it's only needed for fetch, not browser URL
    const params = new URLSearchParams(searchParams);
    params.delete('section_id');

    const cleanParams = params.toString();
    const url = cleanParams ? `${window.location.pathname}?${cleanParams}` : window.location.pathname;

    history.pushState({ searchParams: cleanParams }, '', url);
  }

  showLoading() {
    if (this.loading) {
      this.loading.style.opacity = '1';
      this.loading.style.pointerEvents = 'auto';
    }
  }

  hideLoading() {
    if (this.loading) {
      this.loading.style.opacity = '0';
      this.loading.style.pointerEvents = 'none';
    }
  }
}

// Define custom element
if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}

// Export for use in app.js
export { FacetFiltersForm };
export default FacetFiltersForm;
